import { RadioGroup as RadioGroupPrimitive } from 'radix-ui';
import { type ReactNode, useId } from 'react';
import { cn } from '@/shared/lib/cn';

type RadioCardOption = {
  value: string;
  title: ReactNode;
  /** What choosing this means. Always visible: nobody has to click or hover to read it. */
  description: ReactNode;
  /** Right-hand slot for what the choice changes: a price difference, a delivery estimate. */
  trailing?: ReactNode;
  disabled?: boolean;
};

type RadioCardsProps = Omit<
  React.ComponentProps<typeof RadioGroupPrimitive.Root>,
  'value' | 'defaultValue' | 'onValueChange' | 'orientation' | 'children'
> & {
  options: RadioCardOption[];
  /** The chosen value, or null while nothing is chosen. */
  value: string | null;
  onValueChange: (value: string) => void;
  /** Columns from the small breakpoint up. At phone width the cards always stack. */
  columns?: 1 | 2 | 3;
};

const COLUMNS = { 1: '', 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-3' } as const;

/**
 * A choice among a few options whose consequences need explaining: each option is a card with a title, an
 * explanation that is always visible and an optional trailing slot. A Radix radio group: one tab stop, arrow keys
 * move the selection, a disabled card is skipped. Name it with `Field`, `aria-label` or `aria-labelledby`.
 */
function RadioCards({
  options,
  value,
  onValueChange,
  columns = 1,
  className,
  ...props
}: RadioCardsProps) {
  const id = useId();
  return (
    <RadioGroupPrimitive.Root
      data-slot="radio-cards"
      data-columns={columns}
      value={value ?? ''}
      onValueChange={onValueChange}
      className={cn('group/radio-cards grid grid-cols-1 gap-3', COLUMNS[columns], className)}
      {...props}
    >
      {options.map((option, index) => {
        const titleId = `${id}-${index}-title`;
        const descriptionId = `${id}-${index}-description`;
        const trailingId = `${id}-${index}-trailing`;
        return (
          <RadioGroupPrimitive.Item
            key={option.value}
            value={option.value}
            disabled={option.disabled}
            data-slot="radio-card"
            aria-labelledby={titleId}
            aria-describedby={option.trailing ? `${descriptionId} ${trailingId}` : descriptionId}
            className="flex cursor-pointer items-start gap-3 rounded-sm border border-input bg-card p-4 text-left transition-colors duration-150 ease-standard hover:border-ring/40 disabled:cursor-not-allowed disabled:opacity-50 group-aria-invalid/radio-cards:border-destructive data-[state=checked]:border-primary data-[state=checked]:bg-input-fill"
          >
            <span
              aria-hidden="true"
              className="mt-0.5 grid size-5 shrink-0 place-content-center rounded-full border border-input bg-card"
            >
              <RadioGroupPrimitive.Indicator className="block size-2.5 rounded-full bg-primary" />
            </span>
            <span className="grid min-w-0 flex-1 gap-1">
              <span className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                <span id={titleId} className="font-bold text-heading">
                  {option.title}
                </span>
                {option.trailing ? (
                  <span id={trailingId} className="text-sm text-muted-foreground">
                    {option.trailing}
                  </span>
                ) : null}
              </span>
              <span id={descriptionId} className="text-sm text-muted-foreground">
                {option.description}
              </span>
            </span>
          </RadioGroupPrimitive.Item>
        );
      })}
    </RadioGroupPrimitive.Root>
  );
}

export { type RadioCardOption, RadioCards, type RadioCardsProps };
